"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.graphicsCreatorV2 = exports.graphicsCreatorV2ResultSchema = exports.graphicsCreatorV2ArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Asset schema for input images (logos, products, etc.)
var graphicAssetSchema = zod_1.z.object({
    id: zod_1.z
        .string()
        .describe('Unique human-readable id to reference this asset (e.g., "logo", "product_photo", "background")'),
    type: zod_1.z.literal('image').describe('Type of asset (currently only "image" is supported)'),
    description: zod_1.z
        .string()
        .describe('Description of the asset and optionally where/how it should be used'),
    url: zod_1.z.string().url().describe('Fully qualified URL of the asset'),
});
// Prompt format description for the workflow agent
var imagePromptFormatV2 = "\nA detailed, rich description of the image/ad to create. Be specific and descriptive about visual elements, scenes, and atmosphere.\n\nThis prompt will be passed through a multi-step AI pipeline:\n1. A planner analyzes the request and provided assets to create a structural composition plan\n2. A background image is generated if needed (or uses provided background asset)\n3. The image is analyzed for optimal text/element placement\n4. A style guide is generated based on the image analysis\n5. Additional assets are generated if the planner determines they're needed\n6. Finally, the HTML layout is created\n\nFormat of the prompt should include:\n\n### Goal\nWhat is the purpose of this graphic? (e.g., Facebook ad, Instagram post, banner ad, promotional image)\n\n### Content\n- The main message or headline text (keep it minimal for ads)\n- Any secondary text (CTA, tagline, etc.)\n- Product/service being promoted\n\n### Visual Direction (IMPORTANT - be detailed and specific)\n- Detailed scene description: What should be visible in the background? (e.g., \"modern office with plants and natural light\", \"cozy coffee shop interior with espresso machine and pastries\", \"dynamic gym scene with equipment and energetic atmosphere\")\n- Specific subjects/products: What objects, people, or elements should appear? Be descriptive about their appearance and context\n- Mood or atmosphere: How should it feel? (energetic, calm, professional, playful, etc.)\n- Lighting and style: Natural lighting? Studio lighting? Time of day? Photography style? (commercial photography, lifestyle photography, product photography, etc.)\n- Setting/environment: Where does this take place? What's the context?\n- Visual focus: What should draw attention?\n\nThe more specific and detailed you are about the visual elements, the richer and more interesting the final image will be. Avoid generic descriptions - instead of \"professional background\", describe \"modern workspace with sleek devices, natural lighting, contemporary office environment\".\n\nNote: Do not include specific color schemes or typography - those will be determined automatically based on the generated/provided images.\n";
// Style guide prompt (same as v1, for reference when user provides explicit style)
var styleGuidePromptV2 = "\nThis is an optional style guide that can be passed to the graphics creator.\nOnly pass in styleGuide information if the user has explicitly provided brand colors or styling requirements.\nIt is preferred to leave it empty - the tool will automatically generate a style guide based on the background image analysis.\n\nIf you do provide a style guide, format it as:\n\n## Color Scheme\n- Primary color: #HEXCODE (description)\n- Secondary color: #HEXCODE (description)\n- Accent colors: list if any\n\n## Iconography (optional)\n- Icon styles and any specific icons to use\n";
// Argument schema for V2 - image only for now
exports.graphicsCreatorV2ArgsSchema = zod_1.z.object({
    prompt: zod_1.z.string().describe(imagePromptFormatV2),
    styleGuide: zod_1.z.string().describe(styleGuidePromptV2).nullish(),
    width: zod_1.z.number().describe('Width of the image in pixels').optional().default(1080),
    height: zod_1.z.number().describe('Height of the image in pixels').optional().default(1080),
    assets: zod_1.z
        .array(graphicAssetSchema)
        .describe('Optional array of image assets to use in the graphic (logos, product photos, background images). Each asset needs id, type, description, and fully qualified URL.')
        .nullish(),
    sourceNodeExecutionId: zod_1.z
        .string()
        .describe('Optional node execution ID of a previous graphicsCreatorV2 node to use as a starting point. When provided, the HTML and assets from that execution will be adapted to the new dimensions, enabling efficient creation of the same image in different aspect ratios.')
        .nullish(),
    referenceImageId: zod_1.z
        .enum([
        'energetic-sport',
        'elegant-minimal',
        'bold-colorful',
        'editorial-lifestyle',
        'tech-modern',
        'warm-inviting',
        'youtube-gaming',
        'youtube-vibrant',
        'high-contrast-promo',
        'elegant-product',
        'playful-illustration',
        'retro-event',
        'food-vibrant',
        'automation-tech',
    ])
        .describe('Optional reference image ID for visual inspiration. Available references include diverse styles like energetic-sport, elegant-minimal, bold-colorful, editorial-lifestyle, tech-modern, warm-inviting, plus YouTube thumbnails, promotional graphics, product showcases, illustrated posters, retro events, food ads, and tech visuals. If not provided, the AI will automatically select the most appropriate reference based on the prompt to inspire visually rich and interesting graphics.')
        .nullish(),
    filename: zod_1.z
        .string()
        .describe('Optional custom filename for the graphic image (without extension)')
        .nullish(),
    quality: zod_1.z
        .number()
        .min(1)
        .max(100)
        .describe('Quality/compression level from 1-100, where 100 is highest quality (no compression). Controls PNG compression level. Default is 90.')
        .optional()
        .default(90),
    emitMode: common_1.emitModeSchema.describe('Emit mode for the tool. Default to "single".'),
});
// Result schema
exports.graphicsCreatorV2ResultSchema = zod_1.z.object({
    type: zod_1.z.literal('file'),
    fileType: zod_1.z.literal('image'),
    filename: zod_1.z.string(),
    source: zod_1.z.object({
        kind: zod_1.z.union([zod_1.z.literal('raw'), zod_1.z.literal('url')]),
        value: zod_1.z.string(),
    }),
    html: zod_1.z
        .string()
        .describe('The HTML content used to generate the graphic. Can be reused to recreate the graphic with modifications.')
        .optional(),
    assets: zod_1.z
        .array(graphicAssetSchema)
        .describe('All image assets used in the graphic, including any AI-generated ones.')
        .optional(),
});
// Tool definition
exports.graphicsCreatorV2 = (0, common_1.createEmitModeToolMetadata)({
    id: 'graphicsCreatorV2',
    displayName: 'Image Creator',
    displayDescription: 'Create images and ads with AI-powered planning, background generation, and intelligent layout composition',
    category: 'ai',
    color: common_1.BRAND_COLORS.AI,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.AI),
    icon: 'image-gen',
    argsSchema: exports.graphicsCreatorV2ArgsSchema,
}, exports.graphicsCreatorV2ResultSchema);
